// TODO: Add tax deduction
// Ränteavdraget är idag 30 %.
// Det innebär att staten betalar tillbaka 30 % av dina räntekostnader på deklarationen,
// har du räntekostnader på över 100 000 kr är avdraget 21 % på överskjutande del.
// Detta görs en gång per år om du inte väljer att jämka dem.

import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import "./index.css";

function usePersistentState<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? (JSON.parse(stored) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }, [key, value]);
  return [value, setValue];
}

function NumberInput({
  value,
  onChange,
  step,
  min,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: string;
  min?: string;
}) {
  const [text, setText] = useState(value.toString());
  useEffect(() => setText(value.toString()), [value]);
  return (
    <input
      type="number"
      inputMode="decimal"
      value={text}
      step={step}
      min={min}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        if (t !== "" && !isNaN(Number(t))) onChange(Number(t));
      }}
    />
  );
}

export function App() {
  const [mortgage, setMortgage] = usePersistentState("mortgage", 200000);
  const [rate, setRate] = usePersistentState("rate", 2.2);
  const [years, setYears] = usePersistentState("years", 30);
  const [amortPercent, setAmortPercent] = usePersistentState("amortPercent", 3);
  const [hoa, setHoa] = usePersistentState("hoa", 200);
  const [utilities, setUtilities] = usePersistentState("utilities", 30);
  const [internet, setInternet] = usePersistentState("internet", 30);
  const [insurance, setInsurance] = usePersistentState("insurance", 20);
  const [priceIncrease, setPriceIncrease] = usePersistentState(
    "priceIncrease",
    4
  );
  const [inflation, setInflation] = usePersistentState("inflation", 2);
  const [style, setStyle] = usePersistentState<"annuity" | "straight">(
    "mortgageStyle",
    "annuity"
  );

  // NEW: toggle to apply Swedish tax deduction (ränteavdrag)
  const [taxDeduction, setTaxDeduction] = usePersistentState(
    "taxDeduction",
    true
  );

  const monthlyAnnuityPayment = (
    p: number,
    annualRate: number,
    yrs: number
  ) => {
    const mRate = annualRate / 100 / 12;
    const n = yrs * 12;
    return mRate === 0 ? p / n : (p * mRate) / (1 - Math.pow(1 + mRate, -n));
  };

  const results = useMemo(() => {
    let totalMonths = years * 12;
    if (style === "straight") {
      totalMonths = (100 / amortPercent) * 12;
    }

    const m = monthlyAnnuityPayment(mortgage, rate, years);
    const expValue = mortgage * Math.pow(1 + priceIncrease / 100, years);
    const inflValue = expValue / Math.pow(1 + inflation / 100, years);

    const schedule: {
      month: number;
      interestOnly: number;
      total: number;
      remaining: number;
      amortization: number;
      payments: number;
      taxCredit: number;
    }[] = [];
    const monthlyRate = rate / 100 / 12;
    let balance = mortgage;

    // Track yearly interest for Swedish tiered deduction
    let currentYear = 1;
    let interestThisYear = 0;

    const addMonth = (month: number, interest: number, principal: number) => {
      const basePayments = hoa + utilities + internet + insurance;
      interestThisYear += interest;
      if (month > currentYear * 12) {
        currentYear++;
        interestThisYear = interest;
      }

      let deduction = 0;
      if (taxDeduction) {
        // 30 % up to 100k/year, 21 % on remainder
        const yearlyCap = 100_000;
        const yearInterest = interestThisYear;
        const overCap = Math.max(0, yearInterest - yearlyCap);
        const withinCap = interest - overCap;
        deduction = withinCap * 0.30 + overCap * 0.21;
      }

      schedule.push({
        month,
        interestOnly: interest,
        total: principal + interest + basePayments - deduction,
        remaining: balance,
        amortization: principal,
        payments: basePayments,
        taxCredit: deduction,
      });
    };

    if (style === "annuity") {
      for (let month = 1; month <= totalMonths; month++) {
        const interest = balance * monthlyRate;
        const principal = m - interest;
        balance -= principal;
        addMonth(month, interest, principal);
      }
    } else {
      const yearlyPrincipal = mortgage * (amortPercent / 100);
      const monthlyPrincipal = yearlyPrincipal / 12;
      for (let month = 1; balance > 0.01; month++) {
        const interest = balance * monthlyRate;
        const principal = Math.min(monthlyPrincipal, balance);
        balance -= principal;
        addMonth(month, interest, principal);
      }
    }

    const totalPaid = schedule.reduce((sum, s) => sum + s.total, 0);
    const totalPaidInfl = totalPaid / Math.pow(1 + inflation / 100, years);

    return { expValue, inflValue, totalPaid, totalPaidInfl, schedule };
  }, [
    mortgage,
    rate,
    years,
    amortPercent,
    hoa,
    utilities,
    internet,
    insurance,
    priceIncrease,
    inflation,
    style,
    taxDeduction,
  ]);

  const input = (
    label: string,
    value: number,
    setter: (v: number) => void,
    step?: string,
    min = "0"
  ) => (
    <label className="field">
      <span>{label}</span>
      <NumberInput value={value} onChange={setter} step={step} min={min} />
    </label>
  );

  return (
    <div className="app">
      <h1>Housing Cost</h1>
      <form className="form-grid">
        {input("Total Mortgage ($):", mortgage, setMortgage)}
        {input("Mortgage Rate (% per year):", rate, setRate, "0.01")}
        {style === "annuity"
          ? input("Amortization (years):", years, setYears, "1", "1")
          : input(
              "Amortization (% of loan per year):",
              amortPercent,
              setAmortPercent,
              "0.1",
              "0.1"
            )}
        {input("HOA Fee ($/month):", hoa, setHoa)}
        {input("Electricity/Heating ($/month):", utilities, setUtilities)}
        {input("Internet ($/month):", internet, setInternet)}
        {input("Home Insurance ($/month):", insurance, setInsurance)}
        {input(
          "Expected House Price Increase (%/yr):",
          priceIncrease,
          setPriceIncrease,
          "0.01"
        )}
        {input("Expected Inflation (%/yr):", inflation, setInflation, "0.01")}
        <label className="field">
          <span>Mortgage Style:</span>
          <select
            value={style}
            onChange={(e) => setStyle(e.target.value as any)}
          >
            <option value="annuity">Annuity (U.S.-style fixed payment)</option>
            <option value="straight">Straight amortization (Swedish)</option>
          </select>
        </label>
        {/* NEW checkbox for tax deduction */}
        <label className="field">
          <span>Apply Swedish Tax Deduction:</span>
          <input
            type="checkbox"
            checked={taxDeduction}
            onChange={(e) => setTaxDeduction(e.target.checked)}
          />
        </label>
      </form>

      <section className="result-grid">
        <div>
          Expected Value after term:{" "}
          <strong>${results.expValue.toFixed(0)}</strong>
        </div>
        <div>
          Inflation-Adjusted Value:{" "}
          <strong>${results.inflValue.toFixed(0)}</strong>
        </div>
        <div>
          Total Paid Over Term: <strong>${results.totalPaid.toFixed(0)}</strong>
        </div>
        <div>
          Inflation-Adjusted Total Paid:{" "}
          <strong>${results.totalPaidInfl.toFixed(0)}</strong>
        </div>
      </section>

      <h2>Monthly Cost (after deduction)</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={results.schedule}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            tickFormatter={(m) => (m / 12).toFixed(1)}
            label={{ value: "Years", position: "insideBottom", offset: -5 }}
          />
          <YAxis />
          <Tooltip
            formatter={(v: number) => `$${v.toFixed(0)}`}
            labelFormatter={(m) => `Year ${(m / 12).toFixed(1)}`}
          />
          <Line
            type="monotone"
            dataKey="total"
            stroke="#4caf50"
            dot={false}
            name="Total payment"
          />
          <Line
            type="monotone"
            dataKey="interestOnly"
            stroke="#e67e22"
            dot={false}
            name="Interest portion"
          />
          <Line
            type="monotone"
            dataKey="amortization"
            stroke="#22d9e6ff"
            dot={false}
            name="Amortization"
          />
          <Line
            type="monotone"
            dataKey="taxCredit"
            stroke="#8884d8"
            dot={false}
            name="Tax credit"
          />
        </LineChart>
      </ResponsiveContainer>

      <h2>Remaining Loan Balance</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={results.schedule}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            tickFormatter={(m) => (m / 12).toFixed(1)}
            label={{ value: "Years", position: "insideBottom", offset: -5 }}
          />
          <YAxis />
          <Tooltip
            formatter={(v: number) => `$${v.toFixed(0)}`}
            labelFormatter={(m) => `Year ${(m / 12).toFixed(1)}`}
          />
          <Line
            type="monotone"
            dataKey="remaining"
            stroke="#2196f3"
            dot={false}
            name="Remaining balance"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default App;
